import type { ReviewResponse, CreateReviewRequest, UpdateReviewRequest, UserReviewResponse } from '@/types'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  total: number
  limit: number
  offset: number
}

export function useReviews(clinicId?: string | Ref<string>) {
  const clinicIdRef = clinicId ? (isRef(clinicId) ? clinicId : ref(clinicId)) : ref<string | undefined>(undefined)

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  async function createReview(data: Omit<CreateReviewRequest, 'clinicId'>): Promise<ReviewResponse | null> {
    if (!clinicIdRef.value) {
      error.value = 'ID kliniky není k dispozici'
      return null
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<ReviewResponse>>('/api/reviews', {
        method: 'POST',
        body: {
          clinicId: clinicIdRef.value,
          ...data,
        },
        credentials: 'include',
      })

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se vytvořit hodnocení'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při vytváření hodnocení'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function updateReview(reviewId: string, data: UpdateReviewRequest): Promise<ReviewResponse | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<ReviewResponse>>(`/api/reviews/${reviewId}`, {
        method: 'PUT',
        body: data,
        credentials: 'include',
      })

      if (!response.success || !response.data) {
        error.value = 'Nepodařilo se aktualizovat hodnocení'
        return null
      }

      return response.data
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při aktualizaci hodnocení'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function deleteReview(reviewId: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<void>>(`/api/reviews/${reviewId}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.success) {
        error.value = 'Nepodařilo se smazat hodnocení'
        return false
      }

      return true
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při mazání hodnocení'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function getUserReviews(limit: number = 10, offset: number = 0): Promise<{ reviews: UserReviewResponse[]; total: number } | null> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<PaginatedResponse<UserReviewResponse>>('/api/reviews', {
        query: { limit, offset },
        credentials: 'include',
      })

      if (!response.success) {
        error.value = 'Nepodařilo se načíst hodnocení'
        return null
      }

      return {
        reviews: response.data,
        total: response.total,
      }
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při načítání hodnocení'
      return null
    } finally {
      isLoading.value = false
    }
  }

  async function replyToReview(reviewId: string, content: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const response = await $fetch<ApiResponse<void>>(`/api/reviews/${reviewId}/reply`, {
        method: 'POST',
        body: { content },
        credentials: 'include',
      })

      if (!response.success) {
        error.value = 'Nepodařilo se odeslat odpověď'
        return false
      }

      return true
    } catch (err) {
      const e = err as { data?: { message?: string }; message?: string }
      error.value = e.data?.message || e.message || 'Chyba při odesílání odpovědi'
      return false
    } finally {
      isLoading.value = false
    }
  }

  function clearError() {
    error.value = null
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    createReview,
    updateReview,
    deleteReview,
    getUserReviews,
    replyToReview,
    clearError,
  }
}
